import { createContext, useEffect, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { RolCodigo } from '../types/database.types';

export interface PerfilUsuario {
  id: string;
  salon_id: string;
  nombre: string;
  email: string;
  rol: RolCodigo;
  /**
   * id de `profesionales` cuyo `usuario_id` es este usuario -- `null`
   * para cualquier rol que no sea `profesional`, y también `null` para
   * una profesional cuya cuenta todavía no está enlazada (ver
   * ProfesionalForm.tsx, "Cuenta de acceso") -- en ese caso ninguna de
   * las funciones RLS que dependen de auth_profesional_id() le devuelve
   * nada, así que tampoco tiene sentido que el frontend actúe como si
   * tuviera un profesional_id. Se usa para: fijar a sí misma como
   * profesional al agendar (NuevaCitaForm.tsx) y al vender
   * (VentaForm.tsx), y para armar el enlace "Registrar venta" al marcar
   * una cita como atendida (Agenda.tsx).
   */
  profesionalId: string | null;
}

// `database.types.ts` está escrito a mano (ver el comentario al inicio de
// ese archivo) y no incluye la metadata `Relationships` que sí trae el
// tipo generado por `supabase gen types typescript` -- sin esa metadata,
// TypeScript no puede inferir la forma de un `select` con un embed
// anidado como `roles ( codigo )`, y termina tipando `data` como `never`
// (error real: TS2339 en `data.id`, encontrado corriendo `tsc` de verdad
// por primera vez en el proyecto). `.returns<T>()` es el mecanismo propio
// de supabase-js para estos casos: le dice al cliente qué forma esperar
// en vez de que la intente inferir del string del `select`.
interface PerfilQueryRow {
  id: string;
  salon_id: string;
  nombre: string;
  email: string;
  rol_id: string;
  roles: { codigo: RolCodigo } | null;
}

interface AuthContextValue {
  session: Session | null;
  perfil: PerfilUsuario | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Carga sesión + perfil (salon_id, rol) una sola vez al montar y se
 * mantiene sincronizado con los cambios de sesión de Supabase Auth.
 *
 * El perfil se lee de `usuarios`, no de los metadatos de Auth: en el
 * DDBD, `usuarios.id = auth.uid()` es la fuente de verdad de a qué salón
 * y con qué rol entra cada persona (ver schema.sql, sección 1).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [perfil, setPerfil] = useState<PerfilUsuario | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let activo = true;

    async function cargarPerfil(userId: string) {
      const { data, error } = await supabase
        .from('usuarios')
        .select('id, salon_id, nombre, email, rol_id, roles ( codigo )')
        .eq('id', userId)
        .single()
        .returns<PerfilQueryRow>();

      if (!activo) return;

      if (error || !data) {
        console.error('No se pudo cargar el perfil del usuario:', error);
        setPerfil(null);
        return;
      }

      // El join `roles ( codigo )` viene anidado en la respuesta de PostgREST.
      const rolCodigo = data.roles?.codigo ?? 'clienta';

      // Solo para rol profesional: resolver su propio profesional_id
      // (profesionales.usuario_id = este usuario). `profesionales_read`
      // ya permite a cualquier miembro del salón leer esta tabla, así que
      // esto no necesita ninguna política RLS nueva.
      let profesionalId: string | null = null;
      if (rolCodigo === 'profesional') {
        const { data: prof } = await supabase
          .from('profesionales')
          .select('id')
          .eq('usuario_id', data.id)
          .maybeSingle();
        profesionalId = prof?.id ?? null;
      }

      if (!activo) return;

      setPerfil({
        id: data.id,
        salon_id: data.salon_id,
        nombre: data.nombre,
        email: data.email,
        rol: rolCodigo,
        profesionalId,
      });
    }

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (!activo) return;
      setSession(s);
      if (s?.user) {
        cargarPerfil(s.user.id).finally(() => activo && setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (s?.user) {
        cargarPerfil(s.user.id);
      } else {
        setPerfil(null);
      }
    });

    return () => {
      activo = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  async function signIn(email: string, password: string) {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error ? error.message : null };
  }

  async function signOut() {
    await supabase.auth.signOut();
  }

  return (
    <AuthContext.Provider value={{ session, perfil, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
